/**
 * BulkUploadController - orchestrates Bulk Upload flow
 * Owns: file reading, AI processing, preview rendering, duplicate check,
 * selection, and import orchestration. View remains markup + event binding.
 */

import aiEnhancedBulkUpload from '../../services/AIEnhancedBulkUpload.js';
import InitiativeService from '../../services/InitiativeService.js';
import ApiService from '../../services/ApiService.js';
import EventBus, { AppEvents } from '../../core/EventBus.js';
import { csvParser } from '../../utils/csvParser.js';
import { createBulkUploadStateMachine, BulkUploadStates } from './BulkUploadState.js';
import Logger from '../../utils/Logger.js';

export class BulkUploadController {
  constructor(host) {
    // host: BulkUpload instance (for close()), but state lives here
    this.host = host;
    this.log = Logger.scope('BulkUpload');
    this.fsm = createBulkUploadStateMachine(BulkUploadStates.IDLE);
    this.isOpen = false;
    this.parsedData = null;
    this.duplicateAnalysis = null;
    this.selectedRows = new Set();

    // Expose for inline callbacks in legacy markup (temporary)
    if (typeof window !== 'undefined') {
      window.bulkUploadController = this;
    }
  }

  // ---------- Event entrypoints from view bindings ----------
  onFileSelected(file) { this.processFile(file); }
  onToggleAllRows(checked) { this.toggleAllRows(checked); }
  onCheckDuplicates() { this.checkDuplicates(); }
  onConfirmImport() { this.confirmImport(); }
  onClose() { this.host.close(); }

  // ---------- Core workflow ----------
  async processFile(file) {
    this.setState(BulkUploadStates.READING);
    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      EventBus.emit(AppEvents.TOAST_SHOW, { message: 'File too large. Maximum size is 5MB.', type: 'error' });
      return;
    }
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv')) {
      EventBus.emit(AppEvents.TOAST_SHOW, { message: 'Invalid file type. Please upload a CSV or Excel file.', type: 'error' });
      return;
    }

    try {
      const content = await this.readFile(file);
      this.showProgress('🤖 AI is analyzing and enhancing your data...');
      this.setState(BulkUploadStates.AI_ENHANCING);

      const result = await aiEnhancedBulkUpload.processWithAI(content);
      if (!result.success) {
        EventBus.emit(AppEvents.TOAST_SHOW, { message: result.error, type: 'error' });
        this.hideProgress();
        return;
      }

      this.parsedData = {
        success: true,
        data: result.enhanced,
        errors: result.validation.invalid,
        headers: result.original.headers,
        totalRows: result.summary.totalRows,
        validRows: result.summary.validRows,
        invalidRows: result.summary.invalidRows,
        aiEnhanced: true,
        aiImprovements: result.summary.aiImprovements
      };

      EventBus.emit(AppEvents.TOAST_SHOW, { 
        message: `🎉 AI enhanced ${result.enhanced.length} initiatives successfully!`, 
        type: 'success' 
      });

      this.showPreview(this.parsedData);
      this.setState(BulkUploadStates.PREVIEW);
      this.hideProgress();
    } catch (error) {
      this.log.error('AI processing error:', error);
      EventBus.emit(AppEvents.TOAST_SHOW, { message: 'AI processing failed: ' + error.message, type: 'error' });
      this.hideProgress();
      this.setState(BulkUploadStates.IDLE);
    }
  }

  readFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  showPreview(result) {
    // Reset selection
    this.selectedRows.clear();

    // Hide upload, show preview
    document.getElementById('step-upload').style.display = 'none';
    document.getElementById('step-preview').style.display = 'block';

    // Stats
    const statsEl = document.getElementById('preview-stats');
    let statsHTML = `
      <div class="stat-badges">
        <span class="badge badge-info">Total Rows: ${result.totalRows}</span>
        <span class="badge badge-success">Valid: ${result.validRows}</span>
        <span class="badge badge-danger">Invalid: ${result.invalidRows}</span>`;
    if (result.aiEnhanced) {
      const improvements = result.aiImprovements;
      statsHTML += `<span class="badge badge-ai">🤖 AI Enhanced</span>`;
      if (improvements.titlesGenerated > 0) statsHTML += `<span class="badge badge-ai-detail">Titles: +${improvements.titlesGenerated}</span>`;
      if (improvements.objectivesImproved > 0) statsHTML += `<span class="badge badge-ai-detail">Objectives: +${improvements.objectivesImproved}</span>`;
      if (improvements.keyResultsGenerated > 0) statsHTML += `<span class="badge badge-ai-detail">Key Results: +${improvements.keyResultsGenerated}</span>`;
    }
    statsHTML += `</div>`;
    if (result.aiEnhanced) {
      statsHTML += `
        <div class="ai-summary">
          🤖 <strong>AI Enhancement Summary:</strong> Automatically improved data quality, generated missing fields, and enhanced ${result.data.length} initiatives for better structure and clarity.
        </div>`;
    }
    statsEl.innerHTML = statsHTML;

    // Table
    const headerEl = document.getElementById('preview-header');
    const bodyEl = document.getElementById('preview-body');
    headerEl.innerHTML = `
      <tr>
        <th width="50">Select</th>
        <th width="80">Status</th>
        <th>Title</th>
        <th>Objective</th>
        <th>Owner</th>
        <th>Priority</th>
        
      </tr>`;

    bodyEl.innerHTML = '';
    result.data.forEach((row, index) => {
      const tr = document.createElement('tr');
      tr.className = 'preview-row';
      tr.dataset.index = index;
      const hasError = result.errors.some(e => e.data === row);
      if (hasError) tr.classList.add('row-error');
      tr.innerHTML = `
        <td><input type="checkbox" class="row-checkbox" data-index="${index}" ${!hasError ? 'checked' : ''}></td>
        <td>${hasError ? '<span class="badge badge-danger">❌ Invalid</span>' : '<span class="badge badge-success">✅ Valid</span>'}</td>
        <td class="truncate" title="${this.escapeHtml(row.title)}">${this.escapeHtml(row.title)}</td>
        <td class="truncate" title="${this.escapeHtml(row.objective)}">${this.escapeHtml(row.objective)}</td>
        <td>${this.escapeHtml(row.owner)}</td>
        <td><span class="priority-badge priority-${row.priority}">${row.priority}</span></td>
        <td>${row.type}</td>`;
      bodyEl.appendChild(tr);
      if (!hasError) this.selectedRows.add(index);
    });

    // Errors
    if (result.errors.length > 0) {
      const errorsEl = document.getElementById('validation-errors');
      errorsEl.innerHTML = `
        <h5>⚠️ Validation Errors:</h5>
        <div class="error-list">
          ${result.errors.map(err => `<div class="error-item"><strong>Row ${err.row}:</strong> ${err.errors.join(', ')}</div>`).join('')}
        </div>`;
    }

    // Row selection handlers
    bodyEl.querySelectorAll('.row-checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const index = parseInt(e.target.dataset.index);
        if (e.target.checked) this.selectedRows.add(index); else this.selectedRows.delete(index);
        this.updateImportSummary();
      });
    });

    // Show import step
    document.getElementById('step-import').style.display = 'block';
    this.updateImportSummary();
  }

  toggleAllRows(checked) {
    const checkboxes = document.querySelectorAll('.row-checkbox');
    checkboxes.forEach(cb => {
      cb.checked = checked;
      const index = parseInt(cb.dataset.index);
      if (checked) this.selectedRows.add(index); else this.selectedRows.delete(index);
    });
    this.updateImportSummary();
  }

  async checkDuplicates() {
    if (!this.parsedData || this.selectedRows.size === 0) {
      EventBus.emit(AppEvents.TOAST_SHOW, { message: 'No rows selected to check', type: 'warning' });
      return;
    }
    this.showProgress('Checking for duplicates...');
    this.setState(BulkUploadStates.DUPLICATE_CHECK);
    const selectedInitiatives = Array.from(this.selectedRows).map(i => this.parsedData.data[i]);
    // Local heuristic check (fast)
    const localAnalysis = await csvParser.checkDuplicates(selectedInitiatives);

    // AI-assisted check (best-effort)
    let aiAnalysis = null;
    try {
      aiAnalysis = await this.checkDuplicatesWithAI(selectedInitiatives);
    } catch (e) {
      console.warn('AI duplicate analysis failed:', e?.message || e);
    }

    if (aiAnalysis) {
      const existing = InitiativeService.getInitiatives();
      const byId = new Map(existing.map(x => [x.id, x]));
      const merged = {
        duplicates: [...localAnalysis.duplicates],
        similar: [...localAnalysis.similar],
        summary: { ...localAnalysis.summary }
      };

      (aiAnalysis.duplicates || []).forEach(item => {
        const exists = merged.duplicates.some(d => d.index === item.index && d.existingInitiative?.id === item.existingId);
        if (!exists && byId.get(item.existingId)) {
          merged.duplicates.push({
            index: item.index,
            newInitiative: selectedInitiatives[item.index],
            existingInitiative: byId.get(item.existingId),
            matchType: item.matchType || 'ai_exact',
            reason: item.reason || ''
          });
        }
      });

      (aiAnalysis.similar || []).forEach(item => {
        const exists = merged.similar.some(s => s.index === item.index && s.existingInitiative?.id === item.existingId);
        if (!exists && byId.get(item.existingId)) {
          merged.similar.push({
            index: item.index,
            newInitiative: selectedInitiatives[item.index],
            existingInitiative: byId.get(item.existingId),
            similarity: typeof item.similarity === 'number' ? item.similarity : 0.75,
            matchType: item.matchType || 'ai_similar',
            reason: item.reason || ''
          });
        }
      });

      merged.summary.duplicateCount = merged.duplicates.length;
      merged.summary.similarCount = merged.similar.length;
      merged.summary.uniqueCount = merged.summary.total - merged.summary.duplicateCount;
      this.duplicateAnalysis = merged;
    } else {
      this.duplicateAnalysis = localAnalysis;
    }
    this.hideProgress();
    this.showDuplicateAnalysis();
  }

  /**
   * AI-assisted duplicate detection
   */
  async checkDuplicatesWithAI(selectedInitiatives) {
    const existing = InitiativeService.getInitiatives();
    if (!existing || existing.length === 0) return null;

    const compactSelected = selectedInitiatives.map((i, idx) => ({
      index: idx,
      title: (i.title || '').slice(0, 140),
      objective: (i.objective || '').slice(0, 280)
    }));
    const compactExisting = existing.map(i => ({
      id: i.id,
      title: (i.title || '').slice(0, 140),
      objective: (i.objective || '').slice(0, 280)
    })).slice(0, 400);

    const prompt = `You are assisting a bulk upload. Compare NEW initiatives (by index) with EXISTING initiatives (by id) and flag likely duplicates.

Rules:
- Exact duplicate: same or near-identical title and objective.
- Similar: high semantic overlap.
- Be conservative; only flag when reasonably confident.

Return STRICT JSON only:
{"duplicates":[{"index":0,"existingId":"...","matchType":"exact_title|exact_objective|both","reason":"..."}],"similar":[{"index":0,"existingId":"...","similarity":0.85,"matchType":"title|objective|combined","reason":"..."}]}

NEW=${JSON.stringify(compactSelected)}
EXISTING=${JSON.stringify(compactExisting)}`;

    const resp = await ApiService.call(prompt, { temperature: 0.1, maxTokens: 900, promptKey: 'bulkUploadDuplicateCheck', response_format: { type: 'json_object' } });
    const text = resp?.content || '';
    const match = text.match(/\{[\s\S]*\}/);
    if (!match) return null;
    const parsed = JSON.parse(match[0]);
    parsed.duplicates = Array.isArray(parsed.duplicates) ? parsed.duplicates : [];
    parsed.similar = Array.isArray(parsed.similar) ? parsed.similar : [];
    parsed.similar.forEach(s => { if (typeof s.similarity !== 'number') s.similarity = 0.7; });
    return parsed;
  }

  showDuplicateAnalysis() {
    if (!this.duplicateAnalysis) return;
    document.getElementById('step-duplicates').style.display = 'block';
    const summaryEl = document.getElementById('duplicate-summary');
    const listEl = document.getElementById('duplicate-list');
    const analysis = this.duplicateAnalysis;
    summaryEl.innerHTML = `
      <div class="duplicate-stats">
        <span class="badge badge-info">Total Selected: ${analysis.summary.total}</span>
        <span class="badge ${analysis.summary.duplicateCount > 0 ? 'badge-danger' : 'badge-success'}">Exact Duplicates: ${analysis.summary.duplicateCount}</span>
        <span class="badge ${analysis.summary.similarCount > 0 ? 'badge-warning' : 'badge-success'}">Similar: ${analysis.summary.similarCount}</span>
        <span class="badge badge-success">Unique: ${analysis.summary.uniqueCount}</span>
      </div>`;

    if (analysis.duplicates.length > 0 || analysis.similar.length > 0) {
      listEl.innerHTML = `
        ${analysis.duplicates.map(dup => `
          <div class="duplicate-item duplicate-exact">
            <div class="duplicate-type">❌ Exact Duplicate</div>
            <div class="duplicate-compare">
              <div class="duplicate-new"><strong>New:</strong> ${this.escapeHtml(dup.newInitiative.title)}</div>
              <div class="duplicate-existing"><strong>Existing:</strong> ${this.escapeHtml(dup.existingInitiative.title)}</div>
            </div>
            ${dup.reason ? `<div class="duplicate-reason">Reason: ${this.escapeHtml(dup.reason)}</div>` : ''}
            <button class="btn btn-small btn-danger" onclick="window.bulkUploadController.removeFromSelection(${dup.index})">Remove from Import</button>
          </div>`).join('')}
        ${analysis.similar.map(sim => `
          <div class="duplicate-item duplicate-similar">
            <div class="duplicate-type">⚠️ Similar (${Math.round((sim.similarity || 0.7) * 100)}%)</div>
            <div class="duplicate-compare">
              <div class="duplicate-new"><strong>New:</strong> ${this.escapeHtml(sim.newInitiative.objective)}</div>
              <div class="duplicate-existing"><strong>Existing:</strong> ${this.escapeHtml(sim.existingInitiative.objective)}</div>
            </div>
            ${sim.reason ? `<div class="duplicate-reason">Reason: ${this.escapeHtml(sim.reason)}</div>` : ''}
            <button class="btn btn-small btn-warning" onclick="window.bulkUploadController.removeFromSelection(${sim.index})">Remove from Import</button>
          </div>`).join('')}
      `;
    } else {
      listEl.innerHTML = '<p class="success-message">✅ No duplicates found!</p>';
    }
    document.getElementById('step-import').style.display = 'block';
    this.updateImportSummary();
  }

  removeFromSelection(index) {
    this.selectedRows.delete(index);
    const checkbox = document.querySelector(`.row-checkbox[data-index="${index}"]`);
    if (checkbox) checkbox.checked = false;
    this.updateImportSummary();
    this.checkDuplicates();
  }

  updateImportSummary() {
    const summaryEl = document.getElementById('import-summary');
    if (!summaryEl || !this.parsedData) return;
    const selectedCount = this.selectedRows.size;
    const selectedInitiatives = Array.from(this.selectedRows).map(i => this.parsedData.data[i]);
    summaryEl.innerHTML = `
      <div class="import-ready">
        <h5>Ready to Import:</h5>
        <p class="import-count">${selectedCount} initiatives</p>
        <div class="import-preview">
          <ul>
            ${selectedInitiatives.slice(0, 5).map(init => `<li>${this.escapeHtml(init.title)}</li>`).join('')}
            ${selectedCount > 5 ? `<li>... and ${selectedCount - 5} more</li>` : ''}
          </ul>
        </div>
      </div>`;
  }

  async confirmImport() {
    if (this.selectedRows.size === 0) {
      EventBus.emit(AppEvents.TOAST_SHOW, { message: 'No initiatives selected for import', type: 'warning' });
      return;
    }
    const selectedInitiatives = Array.from(this.selectedRows).map(i => this.parsedData.data[i]);
    this.showProgress(`Importing ${selectedInitiatives.length} initiatives...`);
    this.setState(BulkUploadStates.IMPORTING);

    let successCount = 0;
    let errorCount = 0;
    const importedInitiatives = [];

    // Ensure InitiativeService is ready and persist via service in all environments
    try { if (!InitiativeService.initialized) { await InitiativeService.initialize(); } } catch {}

    // Build an index of existing and to-be-created initiatives by id and title to resolve dependencies by title
    const existing = InitiativeService.getInitiatives() || [];
    const byTitle = new Map(); existing.forEach(it => {
      const k = String((it.title || '').toLowerCase());
      if (!byTitle.has(k)) byTitle.set(k, []); byTitle.get(k).push(it);
    });
    const ambiguous = [];
    const unresolved = [];

    for (let i = 0; i < selectedInitiatives.length; i++) {
      const initiative = selectedInitiatives[i];
      try {
        if (!initiative.id) initiative.id = `init_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        initiative.createdAt = initiative.createdAt || new Date().toISOString();
        initiative.updatedAt = new Date().toISOString();

        await InitiativeService.createInitiative(initiative);
        // Update title index for dependency resolution
        const tkey = String((initiative.title || '').toLowerCase());
        if (!byTitle.has(tkey)) byTitle.set(tkey, []); byTitle.get(tkey).push(initiative);
        importedInitiatives.push(initiative);
        successCount++;
        const percent = ((i + 1) / selectedInitiatives.length) * 100;
        this.updateProgress(percent, `Imported ${i + 1} of ${selectedInitiatives.length}`);
      } catch (error) {
        this.log.warn('InitiativeService error during import:', error);
        errorCount++;
      }
    }

    // Persist dependencies and risks (best-effort)
    try {
      const { setDependenciesFor, setRiskFor } = await import('../../services/GraphService.js');
      for (const row of selectedInitiatives) {
        // Dependencies: resolve ids first, then unique title matches
        const fromId = row.id;
        const refs = Array.isArray(row.dependsOn) ? row.dependsOn : [];
        const resolved = [];
        refs.forEach(ref => {
          const s = String(ref).trim();
          if (!s) return;
          // Prefer explicit IDs
          if (/^init_/.test(s)) { resolved.push(s); return; }
          // Try title exact, choose first (titles are not unique; we skip ambiguities)
          const arr = byTitle.get(s.toLowerCase()) || [];
          if (arr.length === 1) {
            resolved.push(String(arr[0].id));
          } else if (arr.length > 1) {
            ambiguous.push({ fromId, fromTitle: row.title || row.objective || fromId, ref: s, candidates: arr.map(a => ({ id: String(a.id), title: a.title || a.objective || a.id })) });
          } else {
            unresolved.push({ fromId, fromTitle: row.title || row.objective || fromId, ref: s });
          }
        });
        if (resolved.length) {
          try { await setDependenciesFor(fromId, resolved, 'depends_on'); } catch (_) {}
        }
        // Risk
        if (row.riskLevel || row.riskNote) {
          try { await setRiskFor(fromId, { level: row.riskLevel || null, note: row.riskNote || '' }); } catch (_) {}
        }
      }
    } catch (e) {
      console.warn('Dependency/Risk persistence failed:', e?.message || e);
    }

    // Resolve ambiguous/unresolved references (optional step)
    if ((ambiguous.length || unresolved.length) && (import.meta.env?.DEV)) {
      try {
        const { ModalService } = await import('../../ui-kit/ModalService.jsx');
        const allInits = (InitiativeService.getInitiatives() || []).map(i => ({ id: String(i.id), title: i.title || i.objective || i.id }));
        const buildRow = (item, idx, opts) => {
          const options = (opts || allInits).map(o => `<option value="${o.id}">${this.escapeHtml(o.title)}</option>`).join('');
          return `<tr><td>${this.escapeHtml(item.fromTitle)}</td><td>${this.escapeHtml(item.ref)}</td><td><select data-map-index="${idx}"><option value="">-- skip --</option>${options}</select></td></tr>`;
        };
        const rowsA = ambiguous.map((a, i) => buildRow(a, `a-${i}`, a.candidates));
        const rowsU = unresolved.map((u, i) => buildRow(u, `u-${i}`, allInits));
        const html = `
          <div>
            <p>Some "Depends On" references could not be uniquely resolved. Map them below (optional):</p>
            <div style="margin:8px 0;display:flex;gap:8px;align-items:center">
              <label style="font-size:12px;color:var(--text-secondary)">Filter</label>
              <input id="map-filter" class="form-control" placeholder="Search initiatives..." style="flex:1" />
            </div>
            <table class="data-table" style="width:100%;border-collapse:collapse">
              <thead><tr><th>From</th><th>Reference</th><th>Map To</th></tr></thead>
              <tbody>${rowsA.join('')}${rowsU.join('')}</tbody>
            </table>
          </div>`;
        const modal = ModalService.open({
          title: 'Resolve Dependency References',
          content: html,
          actions: [
            { text: 'Skip', class: 'uk-btn uk-btn-secondary', action: () => {} },
            { text: 'Apply', class: 'uk-btn uk-btn-primary', action: async () => {
              const selects = Array.from(document.querySelectorAll('[data-map-index]'));
              const picks = selects.map(s => ({ key: s.getAttribute('data-map-index'), val: s.value }));
              const { addDependenciesFor } = await import('../../services/GraphService.js');
              // interpret picks and add edges
              for (const { key, val } of picks) {
                if (!val) continue;
                if (key.startsWith('a-')) {
                  const idx = parseInt(key.slice(2), 10);
                  const it = ambiguous[idx]; if (it) await addDependenciesFor(it.fromId, [val], 'depends_on');
                } else if (key.startsWith('u-')) {
                  const idx = parseInt(key.slice(2), 10);
                  const it = unresolved[idx]; if (it) await addDependenciesFor(it.fromId, [val], 'depends_on');
                }
              }
            }}
          ]
        });
        // Live filter for unresolved rows
        setTimeout(() => {
          try {
            const input = document.getElementById('map-filter');
            if (input) {
              input.addEventListener('input', () => {
                const q = input.value.toLowerCase();
                const filtered = allInits.filter(o => o.title.toLowerCase().includes(q));
                const optsHtml = (list) => '<option value="">-- skip --</option>' + list.map(o => `<option value="${o.id}">${this.escapeHtml(o.title)}</option>`).join('');
                document.querySelectorAll('select[data-map-index^="u-"]').forEach(sel => {
                  const cur = sel.value;
                  sel.innerHTML = optsHtml(filtered);
                  if (cur && filtered.some(o => o.id === cur)) sel.value = cur;
                });
              });
            }
          } catch (_) {}
        }, 0);
      } catch (e) { console.warn('Resolve mapping dialog failed:', e?.message || e); }
    }

    this.hideProgress();
    if (successCount > 0) {
      EventBus.emit(AppEvents.BULK_IMPORT_COMPLETE, { successCount, errorCount, total: selectedInitiatives.length });
    }
    this.showCompletionStep({ successCount, errorCount, total: selectedInitiatives.length });
    this.setState(BulkUploadStates.COMPLETE);
  }

  showCompletionStep(results) {
    // Hide previous steps
    ['step-upload','step-preview','step-duplicates','step-import'].forEach(id => {
      const el = document.getElementById(id); if (el) el.style.display = 'none';
    });
    const completeEl = document.getElementById('step-complete');
    if (completeEl) completeEl.style.display = 'block';
    const summaryEl = document.getElementById('completion-summary');
    let summaryHTML = `
      <div class="completion-stats">
        <div class="stat-big stat-success">
          <div class="stat-icon">✅</div>
          <div class="stat-content">
            <div class="stat-number">${results.successCount}</div>
            <div class="stat-label">Successfully Imported</div>
          </div>
        </div>`;
    if (results.errorCount > 0) {
      summaryHTML += `
        <div class="stat-big stat-error">
          <div class="stat-icon">❌</div>
          <div class="stat-content">
            <div class="stat-number">${results.errorCount}</div>
            <div class="stat-label">Import Errors</div>
          </div>
        </div>`;
    }
    summaryHTML += `</div><div class="completion-message">`;
    if (results.successCount > 0 && results.errorCount === 0) {
      summaryHTML += `<div class="message-success"><h5>🎉 Perfect! All initiatives imported successfully</h5><p>Your ${results.successCount} initiatives are now available in the Portfolio Analysis view.</p></div>`;
    } else if (results.successCount > 0 && results.errorCount > 0) {
      summaryHTML += `<div class="message-warning"><h5>⚠️ Partially successful import</h5><p>${results.successCount} initiatives imported successfully, but ${results.errorCount} failed to import.</p><p>Check the browser console for error details.</p></div>`;
    } else {
      summaryHTML += `<div class="message-error"><h5>❌ Import failed</h5><p>No initiatives were imported. Please check your data and try again.</p></div>`;
    }
    summaryHTML += `</div><div class="next-steps"><h6>What's Next?</h6><ul><li>View your imported initiatives in the <strong>Portfolio Analysis</strong> tab</li><li>Use <strong>Query Database with AI</strong> to search through your initiatives</li><li>Run <strong>Conflict Analysis</strong> to detect overlaps</li></ul></div>`;
    summaryEl.innerHTML = summaryHTML;

    if (results.successCount > 0) {
      EventBus.emit(AppEvents.TOAST_SHOW, { message: `🎉 Import complete! ${results.successCount} initiatives added`, type: 'success' });
    }
  }

  // ---------- State helpers ----------
  setState(next) {
    const res = this.fsm.setState(next);
    if (!res.ok) {
      this.log.warn('Invalid BulkUpload state transition', res);
      return false;
    }
    this.updateUIForState(res.state);
    return true;
  }

  updateUIForState(state) {
    const steps = ['step-upload','step-preview','step-duplicates','step-import','step-complete'];
    const show = (id, visible) => { const el = document.getElementById(id); if (el) el.style.display = visible ? 'block' : 'none'; };
    steps.forEach(id => show(id, false));
    switch (state) {
      case BulkUploadStates.IDLE:
      case BulkUploadStates.READING:
      case BulkUploadStates.AI_ENHANCING:
        show('step-upload', true);
        break;
      case BulkUploadStates.PREVIEW:
        show('step-preview', true);
        show('step-import', true);
        break;
      case BulkUploadStates.DUPLICATE_CHECK:
        show('step-preview', true);
        show('step-duplicates', true);
        show('step-import', true);
        break;
      case BulkUploadStates.IMPORTING:
        show('step-preview', true);
        show('step-import', true);
        break;
      case BulkUploadStates.COMPLETE:
        show('step-complete', true);
        break;
    }
  }

  // ---------- UI helpers ----------
  showProgress(message) {
    const progressEl = document.getElementById('upload-progress');
    const backdropEl = document.getElementById('upload-progress-backdrop');
    const textEl = document.getElementById('progress-text');
    if (progressEl) progressEl.style.display = 'block';
    if (backdropEl) backdropEl.style.display = 'block';
    if (textEl) textEl.textContent = message;
  }

  updateProgress(percent, message) {
    const fillEl = document.getElementById('progress-fill');
    const textEl = document.getElementById('progress-text');
    if (fillEl) fillEl.style.width = `${percent}%`;
    if (textEl) textEl.textContent = message;
  }

  hideProgress() {
    const progressEl = document.getElementById('upload-progress');
    const backdropEl = document.getElementById('upload-progress-backdrop');
    if (progressEl) progressEl.style.display = 'none';
    if (backdropEl) backdropEl.style.display = 'none';
  }

  escapeHtml(text = '') {
    return String(text).replace(/[&<>"']/g, (m) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#039;'}[m]));
  }
}

export default BulkUploadController;
